import { BROWSER_ITEM_TYPES } from '../constants/WbMediaConstants';

export const getTextFromNameFolder = (name) => name?.split('/').pop();

// A node is visible in the tree view only when every ancestor folder, walking
// up to the implicit root (id 0), is currently expanded. Mirrors how the tree
// component reveals nodes based on the open/expanded set.
export const isNodeVisibleInTree = (node, browserTree, expandedNodes = []) => {
  const expanded = new Set(expandedNodes);
  let parentId = node.parent;
  while (parentId && parentId !== 0) {
    if (!expanded.has(parentId)) return false;
    const parentNode = browserTree.find((n) => n.id === parentId);
    parentId = parentNode ? parentNode.parent : 0;
  }
  return true;
};

// The ids of the media items the user can actually see right now. This is the
// only media "Select all" is ever allowed to select, so it must match exactly
// what each view renders:
// - grid view shows the current folder's direct media (or root-level results
//   while searching), the same filter MediaPaneGridView uses;
// - tree view shows media under fully-expanded ancestor folders.
export const getVisibleMediaIds = ({
  browserTree,
  isGridView,
  isSearching,
  folderNode,
  expandedNodes = [],
}) => {
  const mediaItems = browserTree.filter(
    (item) => item.type === BROWSER_ITEM_TYPES.MEDIA,
  );
  if (isGridView) {
    const folderIdSelected = isSearching ? 0 : folderNode?.id || 0;
    return mediaItems
      .filter((item) => item.parent === folderIdSelected)
      .map((item) => item.id);
  }
  return mediaItems
    .filter((item) => isNodeVisibleInTree(item, browserTree, expandedNodes))
    .map((item) => item.id);
};

export const buildTreeFolders = (folderData) => {
  const build = (node) => {
    const result = [
      {
        id: node.id,
        parent: node.parent_id || 0,
        text: getTextFromNameFolder(node.name),
        data: node,
        droppable: true,
        type: BROWSER_ITEM_TYPES.FOLDER,
      },
    ];

    const children = Object.values(node.children || {});
    for (const child of children) {
      result.push(...build(child));
    }

    return result;
  };

  if (!folderData || Object.keys(folderData).length === 0) return [];

  return build(folderData);
};

export const addNewFolderToTree = (browserTree, { id, name, parent_id }) => {
  const newNode = {
    id,
    parent: parent_id || 0,
    text: getTextFromNameFolder(name),
    data: {
      id,
      name,
      parent_id,
    },
    droppable: true,
    type: BROWSER_ITEM_TYPES.FOLDER,
  };

  const newTree = structuredClone(browserTree);
  newTree.push(newNode);

  const parentNode = newTree.find(
    (n) => n.id === parent_id && n.type === BROWSER_ITEM_TYPES.FOLDER,
  );
  if (parentNode) {
    parentNode.data.children = parentNode.data.children || [];
    parentNode.data.children.push({
      id,
      name,
      parent_id,
    });
  }

  return newTree;
};

export const removeFolderFromTree = (browserTree, folderNode) => {
  const tree = structuredClone(browserTree);

  // Step 1: Get list of IDs to delete (folder + children)
  const toDelete = new Set();
  const queue = [folderNode.id];

  while (queue.length > 0) {
    const currentId = queue.shift();
    toDelete.add(currentId);

    tree.filter((n) => n.parent === currentId).forEach((n) => queue.push(n.id));
  }

  // Step 2: Filter the tree
  const filteredTree = tree.filter((n) => !toDelete.has(n.id));

  // Step 3: Optionally, update parent's data.children
  const parent = filteredTree.find(
    (n) => n.id === folderNode.parent && n.type === BROWSER_ITEM_TYPES.FOLDER,
  );
  if (parent && parent.data?.children) {
    parent.data.children = parent.data.children.filter(
      (c) => !toDelete.has(c.id),
    );
  }

  return filteredTree;
};

export const updateFolderInTree = (browserTree, updatedNode) => {
  const tree = structuredClone(browserTree);

  return tree.map((node) => {
    if (node.id === updatedNode.id) {
      return {
        ...node,
        text: getTextFromNameFolder(updatedNode.name),
        data: {
          ...node.data,
          ...updatedNode,
        },
      };
    }

    // Also update parent's children array if needed
    if (
      node.type === BROWSER_ITEM_TYPES.FOLDER &&
      node.data?.children?.length
    ) {
      node.data.children = node.data.children.map((child) =>
        child.id === updatedNode.id ? { ...child, ...updatedNode } : child,
      );
    }

    return node;
  });
};

export const findParentFromTree = (browserTree, id) => {
  if (!id) return { parentFolder: null, node: null };
  const nodeFromBrowser = browserTree.find((f) => f.data.id === id);
  const parentFolder = browserTree.find((f) => f.id === nodeFromBrowser.parent);

  return { parentFolder, node: nodeFromBrowser };
};

export const canShowLoadMore = ({ browserTree, node, pagination }) => {
  const siblings = browserTree.filter((n) => n.parent === node.parent);
  // Find siblings that are media
  const mediaSiblings = siblings
    .filter((n) => n.type === 'media')
    .sort((a, b) =>
      (a.data.createdAt ?? '').localeCompare(b.data.createdAt ?? ''),
    );
  const isLastMedia =
    mediaSiblings.length > 0 && mediaSiblings.at(-1)?.id === node.id;

  if (!isLastMedia) {
    return false;
  }
  const parentId = node.parent;
  const showLoadMore =
    pagination[parentId]?.page * pagination[parentId]?.pageSize <
    pagination[parentId]?.total;

  return showLoadMore;
};

export const folderNodeShouldGetMedia = ({ pagination, node, isOpen }) =>
  !pagination[node.id] ||
  (!isOpen &&
    pagination[node.id]?.page * pagination[node.id]?.pageSize <
    pagination[node.id]?.total);

export const getFolderNodeForGetMedia = ({ browserTree, node }) => {
  if (!node) {
    const rootNode = browserTree.find((f) => f.data.parent_id === null) || {
      data: {},
      id: null,
      parent_id: null,
    };
    return rootNode;
  }

  return node;
};

export const filterBrowserTreeWhenSearch = ({
  browserTree,
  resetBrowserTree,
}) => {
  if (resetBrowserTree) {
    return [];
  }

  return browserTree;
};

export const filterBrowserTreeAfterDelete = (browserTree, deletedIds) =>
  browserTree.filter((n) => !deletedIds.includes(n.data.id));

export const getDefaultMediaTypeForUpload = (
  mediaType,
  mediaTypes,
  mediaKeys,
) =>
  mediaType.key === mediaKeys.TYPE.MEDIA.KEY
    ? mediaTypes.find((mt) => mt.key === mediaKeys.TYPE.IMAGES.KEY) // Default to images
    : mediaType;

export const findRootNodeOrDefault = (browserTree, folderNode) => {
  const rootNode = browserTree.find((f) => f.parent === 0);
  return folderNode?.id ? folderNode : rootNode;
};

export const shouldCreateRootTag = (browserTree) => !browserTree.length;

// Dispatch Helper Functions
export const dispatchMediaActions = async (dispatch, actions) => {
  const promises = actions.map((action) => dispatch(action));
  return Promise.all(promises);
};

export const createMediaPaginationAction = ({
  folderKey,
  page,
  pageSize,
  total,
  mediaSelectorKey,
  term,
  folderNode,
}) => ({
  type: 'setMediaPagination',
  payload: {
    folderKey: term ? 0 : folderKey || folderNode?.id,
    page,
    pageSize,
    total,
    mediaSelectorKey,
  },
});

export const getDescendantFolderIds = (browserTree, closedIds) => {
  const result = new Set();
  const queue = [...closedIds];
  while (queue.length > 0) {
    const parentId = queue.shift();
    for (const node of browserTree) {
      if (node.parent === parentId && node.type === BROWSER_ITEM_TYPES.FOLDER) {
        result.add(node.id);
        queue.push(node.id);
      }
    }
  }
  return result;
};

export const getSiblingFolderNames = (browserTree, node, excludeSelf = false) => {
  const parent = browserTree.find((n) => n.id === node.parent);
  if (!parent) return [];
  return browserTree
    .filter(
      (n) =>
        n.parent === parent.id && (!excludeSelf || n.id !== node.id),
    )
    .map((n) => n.text.toLowerCase());
};

export const getChildFolderNames = (browserTree, node) =>
  browserTree
    .filter((n) => n.parent === node.id)
    .map((n) => n.text.toLowerCase());

export const canDropOnFolder = ({
  source,
  target,
  browserTree, // eslint-disable-line no-unused-vars
  multiSelect,
  canAddFolder,
}) => {
  const resolvedSource =
    multiSelect && Array.isArray(source) ? source[0] : source;

  if (!resolvedSource || !target) {
    return false;
  }

  if (target.id === resolvedSource.parent) {
    return false;
  }

  if (
    resolvedSource.type === BROWSER_ITEM_TYPES.FOLDER &&
    target.type === BROWSER_ITEM_TYPES.FOLDER &&
    `${target.data.name}/`.startsWith(`${resolvedSource.data.name}/`)
  ) {
    return false;
  }

  if (
    resolvedSource.type === BROWSER_ITEM_TYPES.FOLDER &&
    !canAddFolder(target.id)
  ) {
    return false;
  }

  return (
    (resolvedSource.type === BROWSER_ITEM_TYPES.FOLDER &&
      target.type === BROWSER_ITEM_TYPES.FOLDER) ||
    (resolvedSource.type === BROWSER_ITEM_TYPES.MEDIA &&
      target.type === BROWSER_ITEM_TYPES.FOLDER)
  );
};

export const calculateNodeDepth = (nodeId, tree) => {
  if (!nodeId || nodeId === 0) return 0;

  const node = tree.find((n) => n.id === nodeId);
  if (!node) return 0;

  return 1 + calculateNodeDepth(node.parent, tree);
};
